import { useState } from 'react'
import { Modal, Button, Anchor, LoadingOverlay } from '@mantine/core'
import Box from '@/components/ui/Box'
import AlgoliaSearch from '@/components/ui/AlgoliaSearch'
import {
    CheckCircle,
    XCircle,
    HelpCircle,
} from '@untitled-ui/icons-react'
import classNames from 'classnames'
import dayjs from 'dayjs'

const WARRANTY_OPTIONS = [
    {
        value: 'under_warranty',
        label: 'Sous garantie',
        icon: CheckCircle,
        color: 'text-green-600',
        borderColor: 'border-green-500 bg-green-50',
    },
    {
        value: 'out_of_warranty',
        label: 'Hors garantie',
        icon: XCircle,
        color: 'text-red-600',
        borderColor: 'border-red-500 bg-red-50',
    },
    {
        value: 'unverified',
        label: 'Non vérifiée',
        icon: HelpCircle,
        color: 'text-gray-500',
        borderColor: 'border-gray-300 bg-gray-50',
    },
]

function getWarrantyOption(value) {
    return WARRANTY_OPTIONS.find((o) => o.value === value) || WARRANTY_OPTIONS[2]
}

export default function ServiceCaseWarranty({ serviceCase, updateServiceCase }) {
    const [statusOpened, setStatusOpened] = useState(false)
    const [warrantyOpened, setWarrantyOpened] = useState(false)
    const [value, setValue] = useState(null)
    const [loading, setLoading] = useState(false)

    const current = getWarrantyOption(serviceCase.warranty_status)
    const Icon = current.icon
    const warranty = serviceCase.warranty

    const handleOpenStatus = () => {
        setValue(serviceCase.warranty_status || 'unverified')
        setStatusOpened(true)
    }

    const handleSubmitStatus = async () => {
        setLoading(true)
        await updateServiceCase({ warranty_status: value })
        setLoading(false)
        setStatusOpened(false)
    }

    const handleSelectWarranty = async (selected) => {
        setLoading(true)
        await updateServiceCase({ warranty_code: selected.code || selected.id })
        setLoading(false)
        setWarrantyOpened(false)
    }

    const handleUnlinkWarranty = async () => {
        setLoading(true)
        await updateServiceCase({ warranty_code: null })
        setLoading(false)
    }

    const rightSection = (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            {warranty && (
                <Button
                    variant="default"
                    size="sm"
                    onClick={handleUnlinkWarranty}
                    loading={loading}
                >
                    Désassocier
                </Button>
            )}
            <Button
                variant="default"
                size="sm"
                onClick={handleOpenStatus}
            >
                Statut
            </Button>
        </div>
    )

    return (
        <>
            <Box
                title="Garantie"
                rightSection={rightSection}
                globalAction={true}
                action={() => setWarrantyOpened(true)}
                actionLabel={warranty ? 'Modifier la garantie' : 'Associer une garantie'}
            >
                <div className="flex flex-col gap-2">
                    <div className={`flex items-center gap-2 ${current.color}`}>
                        <Icon width={18} height={18} />
                        <span className="font-medium">{current.label}</span>
                    </div>

                    {warranty && (
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                            <Anchor href={`/warranties/${warranty.code}`} size="sm" fw={500} onClick={(e) => e.stopPropagation()}>
                                {warranty.code}
                            </Anchor>
                            {warranty.product_slug && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span className="text-gray-500">{warranty.product_slug}</span>
                                </>
                            )}
                            {warranty.warranty_start_at && warranty.warranty_end_at && (
                                <>
                                    <span className="text-gray-300">·</span>
                                    <span className="text-gray-500">
                                        {dayjs(warranty.warranty_start_at).format('D MMM YYYY')} → {dayjs(warranty.warranty_end_at).format('D MMM YYYY')}
                                    </span>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </Box>

            {/* Modal statut de garantie */}
            <Modal
                opened={statusOpened}
                onClose={() => setStatusOpened(false)}
                title="Modifier le statut de garantie"
                centered
            >
                <div className="flex flex-col gap-3">
                    {WARRANTY_OPTIONS.map((option) => {
                        const OptionIcon = option.icon
                        const isSelected = option.value === value

                        return (
                            <div
                                key={option.value}
                                className={classNames(
                                    'border rounded-xl p-4 cursor-pointer transition-colors flex items-center gap-3',
                                    isSelected ? option.borderColor : 'border-gray-200 hover:bg-gray-50'
                                )}
                                onClick={() => setValue(option.value)}
                            >
                                <OptionIcon
                                    width={20}
                                    height={20}
                                    className={option.color}
                                />
                                <span className={classNames('font-medium', option.color)}>
                                    {option.label}
                                </span>
                            </div>
                        )
                    })}

                    <Button fullWidth mt="sm" onClick={handleSubmitStatus} loading={loading}>
                        Enregistrer
                    </Button>
                </div>
            </Modal>

            {/* Modal recherche garantie */}
            <Modal
                opened={warrantyOpened}
                onClose={() => setWarrantyOpened(false)}
                title="Associer une garantie"
                centered
            >
                <LoadingOverlay visible={loading} />
                <AlgoliaSearch
                    algolia_index="engine_warranties"
                    placeholder="Rechercher par code de garantie..."
                    onSelect={handleSelectWarranty}
                    label="Garantie"
                />
            </Modal>
        </>
    )
}
